import type { ConfigurationOperation } from '@festina/configuration-general-types/configuration'
import { notEmpty, type Optional } from '@festina-life/core'

type TimeInterval = {
  from: Optional<number>
  to?: Optional<number>
}

const intervalWithFallbacks = (interval: TimeInterval) =>
  ({
    from: notEmpty(interval.from) ? interval.from : Number.NEGATIVE_INFINITY,
    to: notEmpty(interval.to) ? interval.to : Number.POSITIVE_INFINITY,
  }) satisfies TimeInterval

const isIntervalActiveAtDate = (interval: TimeInterval, atDate: Date) => {
  const intervalWithDates = intervalWithFallbacks(interval)

  return (
    intervalWithDates.from <= atDate.getTime() &&
    intervalWithDates.to >= atDate.getTime()
  )
}

export const isDateActive = <T extends ConfigurationOperation>({
  forInterval,
  viewDate,
  operations,
  operationInterval,
}: {
  forInterval: TimeInterval
  viewDate: Date
  operations: Optional<T[]>
  operationInterval: (operation: T) => TimeInterval
}) => {
  const isActiveAtDate = isIntervalActiveAtDate(forInterval, viewDate)

  const isShadowed = (operations ?? []).some(
    function isVersionShadowingDate(version) {
      const versionInterval = operationInterval(version)
      const versionIntervalWithDates = intervalWithFallbacks(versionInterval)

      return (
        isIntervalActiveAtDate(versionInterval, viewDate) &&
        versionIntervalWithDates.from > intervalWithFallbacks(forInterval).from
      )
    },
  )

  return isActiveAtDate && !isShadowed
}
